import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import smsgatewayService from '../../../../../services/smsgateway.service';
import { confirmAction, confirmDelete, showSuccess } from '../../../../../utils/ecxSwal';
import { formatPhoneNumber } from '../../notifications/notificationUtils';
import './FailedMessagesPanel.css';

const PAGE_SIZE = 10;

const escapeHtml = (value = '') =>
    String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

function displayPhoneNumber(value) {
    return formatPhoneNumber(value) || value || '—';
}

function formatCustomerName(message) {
    const parts = [message.customerFirstName, message.customerLastName].filter(Boolean);
    if (parts.length > 0) {
        return parts.join(' ');
    }
    return displayPhoneNumber(message.customer);
}

function getInitials(firstName, lastName, fallbackName) {
    const first = firstName?.trim().charAt(0)?.toUpperCase() || '';
    const last = lastName?.trim().charAt(0)?.toUpperCase() || '';
    if (first || last) {
        return first + last;
    }
    if (fallbackName) {
        const parts = String(fallbackName).trim().split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
        }
        if (parts[0]) {
            return parts[0].charAt(0).toUpperCase();
        }
    }
    return '?';
}

function getAvatarStyle(name) {
    const colors = [
        { bg: '#e8f4fd', text: '#0d6efd' },
        { bg: '#d1f2eb', text: '#198754' },
        { bg: '#cff4fc', text: '#0dcaf0' },
        { bg: '#fff3cd', text: '#ffc107' },
        { bg: '#f8d7da', text: '#dc3545' },
    ];
    const index = name ? name.charCodeAt(0) % colors.length : 0;
    return colors[index];
}

function getStatusBadgeClass(status) {
    const key = String(status || '').toUpperCase();
    if (key === 'UNDELIVERED') return 'text-bg-warning';
    if (key === 'FAILED') return 'text-bg-danger';
    return 'text-bg-secondary';
}

function FailedMessagesPanel({ clinicId, onResendSuccess }) {
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [pageNo, setPageNo] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [resendingId, setResendingId] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [isDeleting, setIsDeleting] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const selectAllRef = useRef(null);

    const clearSelection = useCallback(() => {
        setSelectedIds(new Set());
    }, []);

    const fetchFailed = useCallback(
        (page, append = false) => {
            if (append) {
                setIsLoadingMore(true);
            } else {
                setIsLoading(true);
                clearSelection();
            }

            smsgatewayService
                .getFailedMessages(clinicId, page, PAGE_SIZE)
                .then((res) => {
                    const content = res.data?.content || [];
                    setMessages((prev) => (append ? [...prev, ...content] : content));
                    setTotalElements(res.data?.totalElements ?? content.length);
                    setPageNo(page);
                })
                .catch(() => {
                    if (!append) {
                        setMessages([]);
                        setTotalElements(0);
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                    setIsLoadingMore(false);
                });
        },
        [clinicId, clearSelection]
    );

    useEffect(() => {
        fetchFailed(0, false);
    }, [fetchFailed]);

    const handleResend = async (message) => {
        const phone = displayPhoneNumber(message.customer);
        const result = await confirmAction({
            title: 'Resend message?',
            html: `<p>Resend this message to <strong>${escapeHtml(phone)}</strong>?</p>`,
            iconClass: 'bi bi-arrow-repeat',
            confirmText: 'Resend',
            onConfirm: async () => {
                setResendingId(message.id);
                try {
                    return await smsgatewayService.resendFailedMessages(clinicId, [message.id]);
                } finally {
                    setResendingId(null);
                }
            },
        });

        if (!result.isConfirmed) {
            return;
        }

        showSuccess({
            title: 'Queued for resend',
            text: 'The message has been queued to send again.',
        });
        fetchFailed(0, false);
        onResendSuccess?.();
    };

    const handleResendSelected = async () => {
        const idsToResend = Array.from(selectedIds).filter((id) => id != null);
        if (idsToResend.length === 0) {
            return;
        }

        const count = idsToResend.length;
        const result = await confirmAction({
            title: count === 1 ? 'Resend message?' : `Resend ${count} messages?`,
            html:
                count === 1
                    ? '<p>Resend the selected message?</p>'
                    : `<p>Resend <strong>${count}</strong> selected messages?</p>`,
            iconClass: 'bi bi-arrow-repeat',
            confirmText: 'Resend',
            onConfirm: async () => {
                setIsResending(true);
                try {
                    return await smsgatewayService.resendFailedMessages(clinicId, idsToResend);
                } finally {
                    setIsResending(false);
                }
            },
        });

        if (!result.isConfirmed) {
            return;
        }

        showSuccess({
            title: 'Queued for resend',
            text:
                count === 1
                    ? 'The message has been queued to send again.'
                    : `${count} messages have been queued to send again.`,
        });
        clearSelection();
        fetchFailed(0, false);
        onResendSuccess?.();
    };

    const handleOpenConversation = (customer) => {
        if (!customer) return;
        navigate('/sms-messages', { state: { customer } });
    };

    const allLoadedSelected =
        messages.length > 0 &&
        messages.every((message) => message.id != null && selectedIds.has(message.id));

    const someLoadedSelected =
        messages.some((message) => message.id != null && selectedIds.has(message.id)) &&
        !allLoadedSelected;

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = someLoadedSelected;
        }
    }, [someLoadedSelected, messages.length, selectedIds]);

    const toggleSelect = (id) => {
        if (id == null) {
            return;
        }
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (allLoadedSelected) {
            clearSelection();
            return;
        }
        const loadedIds = messages.map((message) => message.id).filter((id) => id != null);
        setSelectedIds(new Set(loadedIds));
    };

    const handleDeleteSelected = async () => {
        const idsToDelete = Array.from(selectedIds).filter((id) => id != null);
        if (idsToDelete.length === 0) {
            return;
        }

        const count = idsToDelete.length;
        const result = await confirmDelete({
            title: count === 1 ? 'Delete failed message?' : `Delete ${count} failed messages?`,
            itemName: count === 1 ? 'this failed message' : `these ${count} failed messages`,
            description: 'This removes the selected entries from your failed messages list.',
            onConfirm: async () => {
                setIsDeleting(true);
                try {
                    return await smsgatewayService.deleteFailedMessages(clinicId, idsToDelete);
                } finally {
                    setIsDeleting(false);
                }
            },
        });

        if (!result.isConfirmed) {
            return;
        }

        showSuccess({
            title: 'Deleted',
            text:
                count === 1
                    ? 'The failed message was removed.'
                    : `${count} failed messages were removed.`,
        });
        clearSelection();
        fetchFailed(0, false);
        onResendSuccess?.();
    };

    const hasMore = messages.length < totalElements;
    const displayName = (message) => formatCustomerName(message);
    const selectedCount = selectedIds.size;
    const selectionBusy = isDeleting || isResending;

    return (
        <div id="failed-messages-panel" className="card shadow-sm border-0 mt-4 failed-messages-card">
            <div className="card-header bg-white border-bottom py-3">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 w-100">
                    <div className="d-flex align-items-center gap-3">
                        <div className="failed-messages-header-icon">
                            <i className="bi bi-exclamation-octagon-fill"></i>
                        </div>
                        <div>
                            <h6 className="card-title mb-0 fw-normal">Failed messages</h6>
                            <small className="text-muted">
                                Outgoing messages that failed or were undelivered · last 30 days
                            </small>
                        </div>
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        {messages.length > 0 && !isLoading && (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-light-success btn-sm rounded-pill"
                                    onClick={handleResendSelected}
                                    disabled={selectedCount === 0 || selectionBusy}
                                >
                                    {isResending ? (
                                        <Spinner animation="border" size="sm" className="me-1" />
                                    ) : (
                                        <i className="bi bi-arrow-repeat me-1"></i>
                                    )}
                                    Resend{selectedCount > 0 ? ` (${selectedCount})` : ''}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-light-danger btn-sm rounded-pill"
                                    onClick={handleDeleteSelected}
                                    disabled={selectedCount === 0 || selectionBusy}
                                >
                                    {isDeleting ? (
                                        <Spinner animation="border" size="sm" className="me-1" />
                                    ) : (
                                        <i className="bi bi-trash me-1"></i>
                                    )}
                                    Delete{selectedCount > 0 ? ` (${selectedCount})` : ''}
                                </button>
                            </>
                        )}
                        <button
                            type="button"
                            className="btn btn-light-primary btn-sm rounded-pill"
                            onClick={() => fetchFailed(0, false)}
                            disabled={isLoading}
                        >
                            <i className="bi bi-arrow-clockwise me-1"></i>
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            <div className="card-body p-3">
                {isLoading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="text-muted small mt-3 mb-0">Loading failed messages…</p>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="failed-messages-empty text-center py-5">
                        <div className="failed-messages-empty-icon mx-auto mb-3">
                            <i className="bi bi-check2-circle"></i>
                        </div>
                        <h6 className="text-dark fw-normal mb-2">No failed messages</h6>
                        <p className="text-muted small mb-0 mx-auto" style={{ maxWidth: '320px' }}>
                            All outgoing messages were delivered successfully in the last 30 days.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="failed-messages-toolbar d-flex align-items-center gap-2 mb-2 pb-2 border-bottom">
                            <input
                                ref={selectAllRef}
                                type="checkbox"
                                className="form-check-input failed-messages-select-all"
                                id="failed-messages-select-all"
                                checked={allLoadedSelected}
                                onChange={toggleSelectAll}
                                aria-label="Select all loaded failed messages"
                            />
                            <label
                                htmlFor="failed-messages-select-all"
                                className="form-check-label small text-muted mb-0 user-select-none"
                            >
                                Select all
                                {selectedCount > 0 && (
                                    <span className="text-dark fw-normal ms-1">
                                        ({selectedCount} selected)
                                    </span>
                                )}
                            </label>
                        </div>
                        <ul className="list-group list-group-flush failed-messages-list">
                        {messages.map((message) => {
                            const name = displayName(message);
                            const avatarStyle = getAvatarStyle(name);
                            const initials = getInitials(
                                message.customerFirstName,
                                message.customerLastName,
                                name
                            );
                            const isSelected = selectedIds.has(message.id);
                            return (
                                <li
                                    key={message.id}
                                    className={`list-group-item failed-messages-item border-0 px-0 py-3${isSelected ? ' failed-messages-item-selected' : ''}`}
                                >
                                    <div className="d-flex align-items-start gap-3">
                                        <input
                                            type="checkbox"
                                            className="form-check-input failed-messages-item-checkbox flex-shrink-0 mt-2"
                                            checked={isSelected}
                                            onChange={() => toggleSelect(message.id)}
                                            aria-label={`Select message to ${name}`}
                                        />
                                        <div
                                            className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                            style={{
                                                width: '48px',
                                                height: '48px',
                                                backgroundColor: avatarStyle.bg,
                                            }}
                                        >
                                            <span className="fw-normal" style={{ color: avatarStyle.text }}>
                                                {initials}
                                            </span>
                                        </div>
                                        <div className="flex-grow-1 min-w-0">
                                            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-1">
                                                <div className="min-w-0">
                                                    <div className="fw-normal text-dark text-truncate">
                                                        {name}
                                                    </div>
                                                    <div className="text-primary small">
                                                        <i className="bi bi-telephone me-1"></i>
                                                        {displayPhoneNumber(message.customer)}
                                                    </div>
                                                </div>
                                                <span
                                                    className={`badge ${getStatusBadgeClass(message.status)} flex-shrink-0`}
                                                >
                                                    {message.status}
                                                </span>
                                            </div>
                                            <p
                                                className="failed-messages-preview small text-muted mb-2"
                                                title={message.message}
                                            >
                                                {message.message || '—'}
                                            </p>
                                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                                <small className="text-muted">
                                                    <i className="bi bi-clock me-1"></i>
                                                    {message.modifiedAt || message.createdAt || '—'}
                                                </small>
                                                <div className="d-flex gap-2">
                                                    <button
                                                        type="button"
                                                        className="btn btn-light-primary btn-sm rounded-pill"
                                                        onClick={() =>
                                                            handleOpenConversation(message.customer)
                                                        }
                                                        title="Open conversation"
                                                    >
                                                        <i className="bi bi-chat-dots me-1"></i>
                                                        Message
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-light-success btn-sm rounded-pill"
                                                        onClick={() => handleResend(message)}
                                                        disabled={resendingId === message.id || selectionBusy}
                                                    >
                                                        {resendingId === message.id ? (
                                                            <Spinner
                                                                animation="border"
                                                                size="sm"
                                                                className="me-1"
                                                            />
                                                        ) : (
                                                            <i className="bi bi-arrow-repeat me-1"></i>
                                                        )}
                                                        Resend
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                        </ul>
                    </>
                )}
            </div>

            {hasMore && !isLoading && (
                <div className="card-footer bg-light border-top text-center py-3">
                    <button
                        type="button"
                        className="btn btn-light-primary btn-sm rounded-pill"
                        onClick={() => fetchFailed(pageNo + 1, true)}
                        disabled={isLoadingMore}
                    >
                        {isLoadingMore ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Loading…
                            </>
                        ) : (
                            <>
                                <i className="bi bi-chevron-down me-1"></i>
                                Load more ({messages.length.toLocaleString()} of{' '}
                                {totalElements.toLocaleString()})
                            </>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
}

export default FailedMessagesPanel;
